<template>
  <div class="product-list">
    <div class="card">
      <div class="header">
        <h2>📦 Product Management</h2>
        <router-link to="/products/new" class="btn btn-primary">
          ➕ Add New Product
        </router-link>
      </div>
      
      <!-- Search and Filters -->
      <div class="filters">
        <div class="search-section">
          <ProductSearch
            :show-filters="false"
            :show-recent-searches="false"
            :max-results="5"
            search-type="all"
            placeholder="Search by SKU, barcode, or product name..."
            @select="onProductSelect"
            @search="onSearch"
            @clear="onSearchClear"
          />
        </div>
        
        <div class="filter-options">
          <select v-model="statusFilter" @change="handleFilter" class="filter-select">
            <option value="">All Products</option>
            <option value="VALID">Valid Products</option>
            <option value="REVIEW">Review Products</option>
          </select>
          
          <select v-model="typeFilter" @change="handleFilter" class="filter-select">
            <option value="">All Types</option>
            <option value="global">Global Products</option>
            <option value="manual">Manual Products</option>
          </select>
          
          <select v-model="barcodeFilter" @change="handleFilter" class="filter-select">
            <option value="">All Products</option>
            <option value="with">With Barcode</option>
            <option value="without">Without Barcode</option>
          </select>
        </div>
      </div>

      <!-- Statistics Bar -->
      <div class="stats-bar">
        <div class="stat-item">
          <span class="stat-number">{{ totalProducts }}</span>
          <span class="stat-label">Total Products</span>
        </div>
        <div class="stat-item">
          <span class="stat-number">{{ stats.globalProducts }}</span>
          <span class="stat-label">Global Products</span>
        </div>
        <div class="stat-item">
          <span class="stat-number">{{ stats.manualProducts }}</span>
          <span class="stat-label">Manual Products</span>
        </div>
        <div class="stat-item">
          <span class="stat-number">{{ stats.withBarcode }}</span>
          <span class="stat-label">With Barcode</span>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading products...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <p>❌ {{ error }}</p>
        <button @click="fetchProducts" class="btn btn-secondary">
          🔄 Retry
        </button>
      </div>

      <!-- Products Table -->
      <div v-else-if="products.length > 0" class="table-container">
        <table class="table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Product Name</th>
              <th>Barcode</th>
              <th>Type</th>
              <th>Status</th>
              <th>Price</th>
              <th>Stock</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in products" :key="product.id">
              <td>
                <div class="sku-cell">
                  <span class="sku-code">{{ product.sku }}</span>
                </div>
              </td>
              <td>
                <div class="product-name">
                  <strong>{{ product.name }}</strong>
                  <small v-if="product.description" class="description">{{ product.description }}</small>
                </div>
              </td>
              <td>
                <div class="barcode-cell">
                  <code v-if="product.barcode" class="barcode">{{ product.barcode }}</code>
                  <span v-else class="no-barcode">No barcode</span>
                </div>
              </td>
              <td>
                <div class="type-cell">
                  <span class="product-type" :class="product.hasGlobalProduct ? 'global' : 'manual'">
                    <Icon :name="product.hasGlobalProduct ? '🌍' : '📝'" />
                    {{ product.hasGlobalProduct ? 'Global' : 'Manual' }}
                  </span>
                </div>
              </td>
              <td>
                <div class="status-cell">
                  <span v-if="product.globalProduct" 
                        :class="['status-badge', product.globalProduct.status.toLowerCase()]">
                    <Icon :name="product.globalProduct.status === 'VALID' ? '✅' : '⚠️'" />
                    {{ product.globalProduct.status }}
                  </span>
                  <span v-else class="status-badge manual">
                    <Icon name="📝" />
                    Manual
                  </span>
                </div>
              </td>
              <td>
                <span class="price">${{ formatPrice(product.price) }}</span>
              </td>
              <td>
                <span class="stock-badge" :class="getStockClass(product.stockQuantity)">
                  {{ product.stockQuantity || 0 }}
                </span>
              </td>
              <td>
                <span class="date">{{ formatDate(product.createdAt) }}</span>
              </td>
              <td>
                <div class="action-buttons">
                  <router-link 
                    :to="`/products/edit/${product.id}`" 
                    class="btn btn-sm btn-outline"
                    title="Edit Product"
                  >
                    <Icon name="✏️" />
                  </router-link>
                  <button 
                    @click="viewProductDetails(product)" 
                    class="btn btn-sm btn-outline"
                    title="View Details"
                  >
                    <Icon name="👁️" />
                  </button>
                  <button 
                    @click="deleteProduct(product)" 
                    class="btn btn-sm btn-danger"
                    title="Delete Product"
                  >
                    <Icon name="🗑️" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <Icon name="📦" class="empty-icon" />
        <h3>No products found</h3>
        <p class="empty-subtitle">
          {{ searchQuery ? 'No products match your search criteria' : 'Get started by adding your first product' }}
        </p>
        <div class="empty-actions">
          <router-link to="/products/new" class="btn btn-primary">
            <Icon name="➕" />
            Add First Product
          </router-link>
          <button v-if="searchQuery" @click="clearSearch" class="btn btn-outline">
            <Icon name="🔍" />
            Clear Search
          </button>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.pages > 1" class="pagination">
        <button 
          @click="changePage(pagination.page - 1)" 
          :disabled="pagination.page === 1"
          class="btn btn-outline btn-small"
        >
          <Icon name="⬅️" />
          Previous
        </button>
        
        <span class="page-info">
          Page {{ pagination.page }} of {{ pagination.pages }} 
          ({{ totalProducts }} total)
        </span>
        
        <button 
          @click="changePage(pagination.page + 1)" 
          :disabled="pagination.page >= pagination.pages"
          class="btn btn-outline btn-small"
        >
          Next
          <Icon name="➡️" />
        </button>
      </div>
    </div>

    <!-- Product Details Modal -->
    <div v-if="showDetailsModal" class="modal-overlay" @click="closeDetailsModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>Product Details</h3>
          <button @click="closeDetailsModal" class="modal-close">
            <Icon name="✕" />
          </button>
        </div>

        <div class="modal-body" v-if="selectedProduct">
          <div class="product-details">
            <div class="detail-section">
              <h4>Basic Information</h4>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>SKU:</label>
                  <span class="sku-code">{{ selectedProduct.sku }}</span>
                </div>
                <div class="detail-item">
                  <label>Name:</label>
                  <span>{{ selectedProduct.name }}</span>
                </div>
                <div class="detail-item">
                  <label>Description:</label>
                  <span>{{ selectedProduct.description || 'No description' }}</span>
                </div>
                <div class="detail-item">
                  <label>Barcode:</label>
                  <span v-if="selectedProduct.barcode" class="barcode">{{ selectedProduct.barcode }}</span>
                  <span v-else class="no-barcode">No barcode</span>
                </div>
                <div class="detail-item">
                  <label>Type:</label>
                  <span class="product-type" :class="selectedProduct.hasGlobalProduct ? 'global' : 'manual'">
                    <Icon :name="selectedProduct.hasGlobalProduct ? '🌍' : '📝'" />
                    {{ selectedProduct.hasGlobalProduct ? 'Global Product' : 'Manual Product' }}
                  </span>
                </div>
                <div class="detail-item">
                  <label>Price:</label>
                  <span class="price">${{ formatPrice(selectedProduct.price) }}</span>
                </div>
                <div class="detail-item">
                  <label>Stock:</label>
                  <span class="stock-badge" :class="getStockClass(selectedProduct.stockQuantity)">
                    {{ selectedProduct.stockQuantity || 0 }}
                  </span>
                </div>
                <div class="detail-item">
                  <label>Created:</label>
                  <span>{{ formatDateTime(selectedProduct.createdAt) }}</span>
                </div>
              </div>
            </div>

            <div v-if="selectedProduct.globalProduct" class="detail-section">
              <h4>Global Product Information</h4>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Global Name:</label>
                  <span>{{ selectedProduct.globalProduct.name }}</span>
                </div>
                <div class="detail-item">
                  <label>Global Barcode:</label>
                  <span class="barcode">{{ selectedProduct.globalProduct.barcode }}</span>
                </div>
                <div class="detail-item">
                  <label>Status:</label>
                  <span :class="['status-badge', selectedProduct.globalProduct.status.toLowerCase()]">
                    <Icon :name="selectedProduct.globalProduct.status === 'VALID' ? '✅' : '⚠️'" />
                    {{ selectedProduct.globalProduct.status }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-actions">
            <router-link 
              :to="`/products/edit/${selectedProduct.id}`" 
              class="btn btn-primary"
            >
              <Icon name="✏️" />
              Edit Product
            </router-link>
            <button @click="closeDetailsModal" class="btn btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Success/Error Notifications -->
    <div v-if="notification" class="notification" :class="notification.type">
      <Icon :name="notification.type === 'success' ? '✅' : '❌'" />
      <span>{{ notification.message }}</span>
      <button @click="clearNotification" class="notification-close">×</button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import ProductSearch from '@/components/ProductSearch.vue'
import Icon from '@/components/Icon.vue'
import api from '@/services/api'

export default {
  name: 'ProductList',
  components: {
    ProductSearch,
    Icon
  },
  setup() {
    // Reactive data
    const products = ref([])
    const loading = ref(false)
    const error = ref(null)
    const searchQuery = ref('')
    const statusFilter = ref('')
    const typeFilter = ref('')
    const barcodeFilter = ref('')
    const notification = ref(null)
    
    // Pagination
    const pagination = ref({
      page: 1,
      limit: 15,
      total: 0,
      pages: 0
    })

    // Statistics
    const stats = ref({
      globalProducts: 0,
      manualProducts: 0,
      withBarcode: 0
    })

    // Modal state
    const showDetailsModal = ref(false)
    const selectedProduct = ref(null)

    // Computed properties
    const totalProducts = computed(() => pagination.value.total)

    // Methods
    const fetchProducts = async (page = 1) => {
      try {
        loading.value = true
        error.value = null
        
        const params = {
          page,
          limit: pagination.value.limit,
          ...(searchQuery.value && { q: searchQuery.value }),
          ...(statusFilter.value && { status: statusFilter.value }),
          ...(typeFilter.value === 'global' && { hasGlobalProduct: 'true' }),
          ...(typeFilter.value === 'manual' && { hasGlobalProduct: 'false' }),
          ...(barcodeFilter.value === 'with' && { hasBarcode: 'true' }),
          ...(barcodeFilter.value === 'without' && { hasBarcode: 'false' })
        }

        let response
        if (searchQuery.value) {
          // Use search API for queries
          response = await api.get('/search/products', { params })
          products.value = response.data.products
          pagination.value = {
            page: response.data.pagination.currentPage,
            limit: response.data.pagination.limit,
            total: response.data.pagination.totalCount,
            pages: response.data.pagination.totalPages
          }
        } else {
          // Use regular products API for browsing
          response = await api.get('/products', { params })
          products.value = response.data.products || response.data
          
          // Handle different response formats
          if (response.data.pagination) {
            pagination.value = response.data.pagination
          } else {
            pagination.value.total = products.value.length
            pagination.value.pages = Math.ceil(products.value.length / pagination.value.limit)
          }
        }
        
        // Calculate statistics
        calculateStats()
        
      } catch (err) {
        console.error('Error fetching products:', err)
        error.value = err.response?.data?.message || 'Failed to fetch products'
      } finally {
        loading.value = false
      }
    }

    const calculateStats = () => {
      stats.value = {
        globalProducts: products.value.filter(p => p.hasGlobalProduct).length,
        manualProducts: products.value.filter(p => !p.hasGlobalProduct).length,
        withBarcode: products.value.filter(p => p.barcode).length
      }
    }

    const onProductSelect = (product) => {
      // Handle product selection from search
      viewProductDetails(product)
    }

    const onSearch = (searchData) => {
      searchQuery.value = searchData.query
      pagination.value.page = 1
      fetchProducts(1)
    }

    const onSearchClear = () => {
      searchQuery.value = ''
      pagination.value.page = 1
      fetchProducts(1)
    }

    const handleFilter = () => {
      pagination.value.page = 1
      fetchProducts(1)
    }

    const clearSearch = () => {
      searchQuery.value = ''
      statusFilter.value = ''
      typeFilter.value = ''
      barcodeFilter.value = ''
      pagination.value.page = 1
      fetchProducts(1)
    }

    const changePage = (page) => {
      if (page >= 1 && page <= pagination.value.pages) {
        pagination.value.page = page
        fetchProducts(page)
      }
    }

    const viewProductDetails = (product) => {
      selectedProduct.value = product
      showDetailsModal.value = true
    }

    const closeDetailsModal = () => {
      showDetailsModal.value = false
      selectedProduct.value = null
    }

    const deleteProduct = async (product) => {
      const confirmMessage = `Are you sure you want to delete "${product.name}"?\n\nSKU: ${product.sku}\n\nThis action cannot be undone.`
      
      if (!confirm(confirmMessage)) {
        return
      }
      
      try {
        await api.delete(`/products/${product.id}`)
        
        showNotification(`Product "${product.name}" deleted successfully`, 'success')
        
        // Refresh the products list
        fetchProducts(pagination.value.page)
        
      } catch (err) {
        console.error('Error deleting product:', err)
        showNotification(err.response?.data?.message || 'Failed to delete product', 'error')
      }
    }

    const getStockClass = (quantity) => {
      const stock = quantity || 0
      if (stock === 0) return 'out-of-stock'
      if (stock < 10) return 'low-stock'
      return 'in-stock'
    }

    const formatPrice = (price) => {
      const numPrice = parseFloat(price) || 0
      return numPrice.toFixed(2)
    }

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString()
    }

    const formatDateTime = (dateString) => {
      return new Date(dateString).toLocaleString()
    }

    // Notifications
    const showNotification = (message, type = 'info') => {
      notification.value = { message, type }
      setTimeout(() => {
        notification.value = null
      }, 5000)
    }

    const clearNotification = () => {
      notification.value = null
    }

    // Lifecycle
    onMounted(() => {
      fetchProducts()
    })

    return {
      products,
      loading,
      error,
      searchQuery,
      statusFilter,
      typeFilter,
      barcodeFilter,
      pagination,
      totalProducts,
      stats,
      showDetailsModal,
      selectedProduct,
      notification,
      fetchProducts,
      onProductSelect,
      onSearch,
      onSearchClear,
      handleFilter,
      clearSearch,
      changePage,
      viewProductDetails,
      closeDetailsModal,
      deleteProduct,
      getStockClass,
      formatPrice,
      formatDate,
      formatDateTime,
      showNotification,
      clearNotification
    }
  }
}
</script>

<style scoped>
.product-list {
  max-width: 1400px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.filters {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.search-section {
  flex: 1;
  min-width: 300px;
}

.filter-options {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.filter-select {
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  min-width: 140px;
}

.filter-select:focus {
  outline: none;
  border-color: #667eea;
}

/* Statistics Bar */
.stats-bar {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.stat-item {
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 1.5rem;
  font-weight: bold;
  color: #2d3748;
}

.stat-label {
  display: block;
  font-size: 0.875rem;
  color: #718096;
  margin-top: 0.25rem;
}

.loading-state {
  text-align: center;
  padding: 3rem;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-state {
  text-align: center;
  padding: 3rem;
  color: #e74c3c;
}

.table-container {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1000px;
}

.table th {
  background: #f8f9fa;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
}

.table td {
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
}

.table tr:hover {
  background: #f8f9fa;
}

/* Cell Styles */
.sku-cell {
  min-width: 120px;
}

.sku-code {
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-weight: 600;
  font-size: 0.875rem;
}

.product-name {
  min-width: 200px;
}

.product-name strong {
  display: block;
  margin-bottom: 0.25rem;
  color: #2d3748;
}

.description {
  color: #718096;
  font-size: 0.875rem;
  line-height: 1.4;
}

.barcode-cell {
  min-width: 140px;
}

.barcode {
  background: #f7fafc;
  padding: 0.375rem 0.75rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  color: #4a5568;
}

.no-barcode {
  color: #a0aec0;
  font-style: italic;
  font-size: 0.875rem;
}

.type-cell {
  min-width: 100px;
}

.product-type {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
}

.product-type.global {
  background: #e8f5e8;
  color: #2e7d32;
}

.product-type.manual {
  background: #fff3e0;
  color: #ef6c00;
}

.status-cell {
  min-width: 100px;
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-badge.valid {
  background: #c6f6d5;
  color: #276749;
}

.status-badge.review {
  background: #fed7cc;
  color: #c05621;
}

.status-badge.manual {
  background: #f7fafc;
  color: #4a5568;
}

.price {
  font-weight: 600;
  color: #2d3748;
  font-size: 1rem;
}

.stock-badge {
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  min-width: 40px;
  text-align: center;
  display: inline-block;
}

.in-stock {
  background: #c6f6d5;
  color: #276749;
}

.low-stock {
  background: #fed7cc;
  color: #c05621;
}

.out-of-stock {
  background: #fed7d7;
  color: #c53030;
}

.date {
  color: #718096;
  font-size: 0.875rem;
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  min-width: 120px;
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #718096;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  color: #a0aec0;
}

.empty-state h3 {
  margin: 0 0 1rem 0;
  color: #4a5568;
}

.empty-subtitle {
  margin: 0 0 2rem 0;
  font-size: 1rem;
}

.empty-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1rem 0;
  border-top: 1px solid #e2e8f0;
}

.page-info {
  font-size: 0.875rem;
  color: #718096;
  margin: 0 1rem;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  margin: 0;
  color: #2d3748;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #a0aec0;
  padding: 0.25rem;
  border-radius: 4px;
}

.modal-close:hover {
  color: #718096;
  background: #f7fafc;
}

.modal-body {
  padding: 1.5rem;
}

.product-details {
  margin-bottom: 2rem;
}

.detail-section {
  margin-bottom: 2rem;
}

.detail-section h4 {
  margin: 0 0 1rem 0;
  color: #2d3748;
  border-bottom: 2px solid #f7fafc;
  padding-bottom: 0.5rem;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.detail-item label {
  font-weight: 600;
  color: #718096;
  font-size: 0.875rem;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  border-top: 1px solid #e2e8f0;
  padding-top: 1.5rem;
}

/* Notification */
.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  background: white;
  border-radius: 8px;
  padding: 15px 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.notification.success {
  border-left: 4px solid #48bb78;
}

.notification.error {
  border-left: 4px solid #f56565;
}

.notification-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: #a0aec0;
  padding: 0;
  margin-left: 10px;
}

/* Button Styles */
.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  text-decoration: none;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a67d8;
}

.btn-secondary {
  background: #718096;
  color: white;
}

.btn-secondary:hover {
  background: #4a5568;
}

.btn-danger {
  background: #f56565;
  color: white;
}

.btn-danger:hover {
  background: #e53e3e;
}

.btn-outline {
  background: transparent;
  color: #667eea;
  border: 2px solid #667eea;
}

.btn-outline:hover {
  background: #667eea;
  color: white;
}

.btn-sm {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

.btn-small {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .table {
    min-width: 800px;
  }
  
  .table th:nth-child(8),
  .table td:nth-child(8) {
    display: none;
  }
}

@media (max-width: 768px) {
  .filters {
    flex-direction: column;
  }
  
  .search-section {
    min-width: auto;
  }
  
  .filter-options {
    justify-content: center;
  }
  
  .header {
    flex-direction: column;
    gap: 1rem;
  }
  
  .stats-bar {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .table {
    min-width: 600px;
  }
  
  .table th:nth-child(4),
  .table td:nth-child(4),
  .table th:nth-child(7),
  .table td:nth-child(7),
  .table th:nth-child(8),
  .table td:nth-child(8) {
    display: none;
  }
  
  .pagination {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .empty-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .modal {
    width: 95%;
    margin: 1rem;
  }
  
  .detail-grid {
    grid-template-columns: 1fr;
  }
  
  .modal-actions {
    flex-direction: column;
  }
}
</style> 